import axios from 'axios';
import { queryClient } from '../query-client';
import { useAuthStore } from '../auth-store';
import { resumeOnlineSession } from '../api';
import { OFFLINE_TOKEN, useOfflineStore } from './state';
import { syncNow } from './sync';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';
const CHECK_OFFLINE_MS = 10_000;
const CHECK_ONLINE_MS = 30_000;

let timer: number | null = null;
let started = false;

/** Can the API actually be reached? `navigator.onLine` only knows about Wi-Fi. */
async function reachable(): Promise<boolean> {
  try {
    const res = await axios.get(`${BASE_URL}/healthz`, { timeout: 5000 });
    return res.status === 200;
  } catch {
    return false;
  }
}

/** Called when a real request fails, without waiting for the next check. */
export function markOffline(): void {
  const store = useOfflineStore.getState();
  if (store.online) {
    store.set({ online: false });
    schedule(CHECK_OFFLINE_MS);
  }
}

async function comeBackOnline(): Promise<void> {
  const auth = useAuthStore.getState();
  if (auth.user && auth.token === OFFLINE_TOKEN) {
    // A PIN session can't reach the server. Swap it for a real one if the
    // refresh cookie still belongs to this cashier; otherwise they sign in.
    const resumed = await resumeOnlineSession(auth.user.id);
    useOfflineStore.getState().set({ needsSignIn: !resumed });
  } else {
    useOfflineStore.getState().set({ needsSignIn: false });
  }
  void queryClient.invalidateQueries();
  await syncNow();
}

async function check(): Promise<void> {
  const wasOnline = useOfflineStore.getState().online;
  const now = await reachable();
  useOfflineStore.getState().set({ online: now });
  if (now && !wasOnline) {
    await comeBackOnline();
  } else if (now) {
    await syncNow();
  } else if (wasOnline) {
    void queryClient.invalidateQueries();
  }
  schedule(now ? CHECK_ONLINE_MS : CHECK_OFFLINE_MS);
}

function schedule(ms: number): void {
  if (timer !== null) window.clearTimeout(timer);
  timer = window.setTimeout(() => void check(), ms);
}

/** Watch the connection. Called once a pack is on the device. */
export function startConnectivityWatch(): Promise<void> {
  if (started) return Promise.resolve();
  started = true;
  window.addEventListener('online', () => void check());
  window.addEventListener('offline', () => void check());
  return check();
}

/** After a real sign-in: sends what is waiting straight away. */
export async function afterSignIn(): Promise<void> {
  useOfflineStore.getState().set({ needsSignIn: false, online: true });
  await syncNow();
}
